import React, { useState, useCallback } from 'react';
import { Document, Page } from 'react-pdf';
import './PdfPreviewModal.scss';

interface PdfPreviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    invoiceId: string | null;
    quoteId: string | null;
    invoiceFilename?: string;
    quoteFilename?: string;
}

interface PdfPanelProps {
    documentId: string | null;
    title: string;
    noDocumentMessage?: string;
}

const PdfPanel: React.FC<PdfPanelProps> = ({ documentId, title, noDocumentMessage }) => {
    const [numPages, setNumPages] = useState<number | null>(null);
    const [pageNumber, setPageNumber] = useState(1);

    const handleLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
    }, []);

    if (!documentId) {
        return (
            <div className="pdf-modal-panel pdf-modal-panel--empty">
                <div className="pdf-modal-panel__header">{title}</div>
                <div className="pdf-modal-panel__placeholder">
                    {noDocumentMessage || 'No document available'}
                </div>
            </div>
        );
    }

    const fileUrl = `http://localhost:5003/api/documents/${documentId}/download?token=${localStorage.getItem('auth_token')}`;

    return (
        <div className="pdf-modal-panel">
            <div className="pdf-modal-panel__header">
                <span>{title}</span>
                {numPages && numPages > 1 && (
                    <div className="pdf-modal-panel__nav">
                        <button
                            onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                            disabled={pageNumber <= 1}
                        >
                            ‹
                        </button>
                        <span>{pageNumber} / {numPages}</span>
                        <button
                            onClick={() => setPageNumber(p => Math.min(numPages, p + 1))}
                            disabled={pageNumber >= numPages}
                        >
                            ›
                        </button>
                    </div>
                )}
            </div>
            <div className="pdf-modal-panel__content">
                <Document
                    file={fileUrl}
                    onLoadSuccess={handleLoadSuccess}
                    loading={<div className="pdf-modal-panel__loading">Loading...</div>}
                    error={<div className="pdf-modal-panel__error">Failed to load PDF</div>}
                >
                    <Page
                        pageNumber={pageNumber}
                        scale={1.3}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                    />
                </Document>
            </div>
        </div>
    );
};

const PdfPreviewModal: React.FC<PdfPreviewModalProps> = ({
    isOpen,
    onClose,
    invoiceId,
    quoteId,
    invoiceFilename,
    quoteFilename
}) => {
    if (!isOpen) return null;

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="pdf-preview-modal" onClick={handleBackdropClick}>
            <div className="pdf-preview-modal__container">
                <div className="pdf-preview-modal__header">
                    <h2>Document Comparison</h2>
                    <button className="pdf-preview-modal__close" onClick={onClose}>
                        ✕
                    </button>
                </div>
                <div className="pdf-preview-modal__body">
                    <PdfPanel
                        documentId={invoiceId}
                        title={invoiceFilename ? `Invoice: ${invoiceFilename}` : 'Invoice'}
                        noDocumentMessage="Invoice not available"
                    />
                    <PdfPanel
                        documentId={quoteId}
                        title={quoteFilename ? `Quote: ${quoteFilename}` : 'Quote / RFQ'}
                        noDocumentMessage="Quote not available"
                    />
                </div>
            </div>
        </div>
    );
};

export default PdfPreviewModal;
